import 'core-js/stable'; // polyfills for ECMAScript up to 2020
import 'regenerator-runtime/runtime'; // Standalone runtime for Regenerator-compiled generator and async functions

import * as bootstrap from 'bootstrap';
(window as any).bootstrap = bootstrap;

import moment from 'moment';
import 'moment/dist/locale/en-gb';
import 'moment/dist/locale/cy';
import 'moment/dist/locale/vi';
import logger from '@/Utils/logger';
import redirectHelper from '@/Utils/redirectHelper';
import languageHelper from '@/Utils/languageHelper';
import currentUserService from '@/Services/currentUserService';
import resourceDataService from '@/Services/resourceDataService';
import systemSettingsService from '@/Services/systemSettingsService';
import { ErrorCode } from '@/Types/Enums/errorCode';
import { PortalStartupErrorType } from '@/Types/Enums/portalStartupErrorType';
import { applyDefaultVueValidationRules } from '@/Validation/validationRules';
import { ApplicationError } from '@/Services/Clients/applicationError';
import TokenManagerSettings from '@/Models/tokenManagerSettings';
import PortalSettings from '@/Types/portalSettings';
import ContextState from '@/VueCore/stores/Models/contextState';
import UserModel from '@/Models/userModel';

import '@/Utils/windowErrorHandler';

class PortalStartup {
  contextData: ContextState;

  constructor() {
    this.contextData = new ContextState();
    this.contextData.isLoading = true;
  }

  async init(authTokenLanguageDetails: TokenManagerSettings): Promise<ContextState | undefined> {

    const portalStartupErrorType: PortalStartupErrorType = await this.retrieveAndPersistUserData(authTokenLanguageDetails);
    if (portalStartupErrorType !== PortalStartupErrorType.None) {

      this.contextData.portalStartupError = portalStartupErrorType;
      this.contextData.isLoading = false;
      return this.contextData;
    }

    return this.retrieveAndPersistResourceData()
      .then(() => this.retrieveAndPersistPortalSettings(this.contextData))
      .then(() => {
        this.setMomentLocale();
        applyDefaultVueValidationRules();
        this.consoleLogInfo(this.contextData.portalSettings, this.contextData.languageCode);
        return this.contextData;
      })
      .catch((error:any) => {
        const applicationError = new ApplicationError(error);
        this.logUnexpectedError(applicationError);
        this.contextData.portalStartupError = PortalStartupErrorType.UnknownError;
        this.contextData.isLoading = false;
        return Promise.resolve(this.contextData);
      });
  }

  private retrieveAndPersistUserData(authTokenLanguageDetails: TokenManagerSettings): Promise<PortalStartupErrorType> {
    const errorCodesToIgnore = [ErrorCode.BusinessDisabled, ErrorCode.UnknownUser];
    return currentUserService.getUserData(errorCodesToIgnore)
      .then(user => {
        const userData = new UserModel(user);
        console.groupCollapsed('Authenticated user details');
        console.info('Name:', userData.displayName);
        console.info('Email address:', userData.emailAddress);
        console.info('Business:', userData.business.businessName);
        console.info('Is fully registered:', userData.isFullyRegistered);
        console.info('Can complete registration:', userData.canCompleteRegistration);
        console.groupEnd();

        // return PortalStartupErrorType.BusinessDisabled;
        if (!userData.isFullyRegistered) {
          if (userData.canCompleteRegistration) {
            console.info(
              `The user ${userData.displayName} (${userData.emailAddress}) is not fully registered
                so redirecting to the registration page`
            );
            redirectHelper.redirectToPath('/registration.html?id=' + userData.registrationId);
            return Promise.reject(PortalStartupErrorType.UnknownErrorGettingUserData);
          } else {
            logger.logAndThrowUntranslatedError(
              'There is a problem with your user account. Please try again later.',
              'The user with email address \'' + userData.emailAddress + '\' is not correctly registered.');
            return Promise.reject(PortalStartupErrorType.UnknownErrorGettingUserData);
          }
        }

        const languageDetailsArray = [authTokenLanguageDetails, userData.languageDetails];
        const languageCode = languageHelper.getNewestLanguageCodeOrFirstSupported(languageDetailsArray);

        this.contextData.languageCode = languageCode;
        this.contextData.userData = userData;

        return PortalStartupErrorType.None;
      })
      .catch(error => {
        this.contextData.isLoading = false;
        const applicationError = new ApplicationError(error);
        if (applicationError.innerError?.status === 403 && applicationError.errorData.errorCode === ErrorCode.BusinessDisabled) {
          return PortalStartupErrorType.BusinessDisabled;
        }

        if (applicationError.innerError?.status === 403 && applicationError.errorData.errorCode === ErrorCode.UnknownUser) {
          return PortalStartupErrorType.UnknownUser;
        }

        if (applicationError.innerError?.status === 401) {
          return PortalStartupErrorType.AuthorisationError;
        }

        let errorMessage = 'An error occurred loading the site. Please try again later. ';

        if (applicationError.errorData?.errorMessages) {
          errorMessage += applicationError.errorData.errorMessages.join(' ');
        }

        if (applicationError.innerError?.status === 404) {
          errorMessage += 'A user account doesn\'t exist or is incorrectly configured.';
        }

        logger.untranslatedError(
          errorMessage,
          null,
          applicationError);

        return PortalStartupErrorType.UnknownErrorGettingUserData;
      });
  }

  private retrieveAndPersistResourceData(): Promise<void> {
    return resourceDataService.getResourceData(this.contextData.languageCode)
      .then(resourceData => {
        this.contextData.resourceData = resourceData;
      })
      .catch(error => {
        const applicationError = new ApplicationError(error);
        logger.logAndThrowUntranslatedError(
          'An error occurred loading the site. Please try again later.',
          'Unable to get the resource data / translated text',
          applicationError);

        return Promise.reject(applicationError);
      });
  }

  private retrieveAndPersistPortalSettings(contextData: ContextState): Promise<void> {
    return systemSettingsService.getPortalSettings()
      .then((portalSettings: PortalSettings) => {
        contextData.inReleaseMode = portalSettings.inReleaseMode;
        contextData.portalSettings = portalSettings;
        contextData.availableLanguages = languageHelper.getAvailableLanguages(portalSettings.availableLanguageCodes);
      })
      .catch((error: any) => {
        const applicationError = new ApplicationError(error);
        logger.logAndThrowUntranslatedError(
          'An error occurred loading the site. Please try again later.',
          'Unable to get the system portal settings',
          error);

        return Promise.reject(applicationError);
      });
  }

  private setMomentLocale() {
    moment.locale(this.contextData.languageCode);
  }

  private consoleLogInfo(portalSettings: PortalSettings, languageCode: string): void {
    console.info('Thanks for visiting!');
    console.info('Version:', portalSettings.versionNumber);
    console.info('In release mode:', portalSettings.inReleaseMode);
    console.info('Language code:', languageCode);
  }

  private logUnexpectedError(error: ApplicationError) {
    logger.logAndThrowUntranslatedError(
      'An unexpected error occurred loading the site. Please try again later.',
      'An unexpected error occurred. Please review the error and determine if this needs to specifically be caught',
      error);
  }
}

export default new PortalStartup();
